import React, { useMemo, useState } from 'react';
import { PlusIcon, UsersIcon, ListTodoIcon, SearchIcon, ZapIcon, PackageIcon, CheckCircleIcon, ClockIcon, PlayCircleIcon, CalendarIcon } from 'lucide-react';
import { CustomerCard, Customer } from '../components/CustomerCard';
import { TaskCard, Task } from '../components/TaskCard';
import { Button } from '../components/Button';
interface CustomerManagementProps {
  customers: Customer[];
  tasks: Task[];
  onOpenCustomerModal: () => void;
  onOpenTaskModal: () => void;
  onTaskStatusChange: (taskId: string, newStatus: Task['status']) => void;
  onEditCustomer: (customer: Customer) => void;
  onEditTask: (task: Task) => void;
  onDeleteCustomer: (customer: Customer) => void;
  onDeleteTask: (task: Task) => void;
  onViewCustomerDetail: (customerId: string) => void;
  viewMode?: 'customers' | 'tasks';
}
export function CustomerManagement({
  customers,
  tasks,
  onOpenCustomerModal,
  onOpenTaskModal,
  onTaskStatusChange,
  onEditCustomer,
  onEditTask,
  onDeleteCustomer,
  onDeleteTask,
  onViewCustomerDetail,
  viewMode = 'customers'
}: CustomerManagementProps) {
  const [customerSearch, setCustomerSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [taskFilter, setTaskFilter] = useState<'Ladeboks' | 'Andet'>('Ladeboks');
  const [statusFilter, setStatusFilter] = useState<Task['status'] | 'all'>('all');
  // Check if callbacks are no-ops (Read-Only mode)
  const isReadOnly = onOpenCustomerModal.toString().includes('!isReadOnly');
  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers;
    const query = customerSearch.toLowerCase();
    return customers.filter(customer => customer.name.toLowerCase().includes(query) || customer.customerId.toLowerCase().includes(query) || customer.phone.toLowerCase().includes(query) || customer.email?.toLowerCase().includes(query) || customer.address.toLowerCase().includes(query));
  }, [customers, customerSearch]);
  // Filter tasks based on search, category AND status
  const filteredTasks = useMemo(() => {
    // First filter by category
    let filtered = tasks.filter(task => task.category === taskFilter);
    // Then filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }
    // Finally filter by search
    if (!taskSearch.trim()) return filtered;
    const query = taskSearch.toLowerCase();
    return filtered.filter(task => task.title.toLowerCase().includes(query) || task.customerName?.toLowerCase().includes(query) || task.description.toLowerCase().includes(query));
  }, [tasks, taskSearch, taskFilter, statusFilter]);
  // Count tasks by status for current category
  const statusCounts = useMemo(() => {
    const categoryTasks = tasks.filter(task => task.category === taskFilter);
    return {
      all: categoryTasks.length,
      done: categoryTasks.filter(t => t.status === 'done').length,
      'in-progress': categoryTasks.filter(t => t.status === 'in-progress').length,
      planned: categoryTasks.filter(t => t.status === 'planned').length,
      pending: categoryTasks.filter(t => t.status === 'pending').length,
      todo: categoryTasks.filter(t => t.status === 'todo').length
    };
  }, [tasks, taskFilter]);
  // Render customers only view
  if (viewMode === 'customers') {
    return <div data-id="element-1574">
        <header className="mb-8" data-id="element-1575">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1576">Kunde data</h1>
          <p className="text-stone-600" data-id="element-1577">
            Hold styr på dine kunder og deres information
          </p>
        </header>

        <section data-id="element-1578">
          <div className="flex items-center justify-between mb-6" data-id="element-1579">
            <div className="flex items-center gap-3" data-id="element-1580">
              <div className="p-2 bg-teal-100 rounded-lg" data-id="element-1581">
                <UsersIcon className="w-5 h-5 text-teal-700" data-id="element-1582" />
              </div>
              <div data-id="element-1583">
                <h2 className="text-2xl font-semibold text-stone-900" data-id="element-1584">
                  Kunder
                </h2>
                <p className="text-sm text-stone-500" data-id="element-1585">
                  {filteredCustomers.length} af {customers.length}
                </p>
              </div>
            </div>
            {!isReadOnly && <Button onClick={onOpenCustomerModal} size="sm" className="flex items-center gap-2" data-id="element-1586">
                <PlusIcon className="w-4 h-4" data-id="element-1587" />
                Tilføj kunde
              </Button>}
          </div>

          {/* Customer Search */}
          {customers.length > 0 && <div className="mb-4 relative" data-id="element-1588">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-1589" />
              <input type="text" placeholder="Søg efter kunde..." value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} className="w-full pl-11 pr-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1590" />
            </div>}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1591">
            {customers.length === 0 ? <div className="col-span-full bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1592">
                <UsersIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1593" />
                <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1594">
                  Ingen kunder endnu
                </h3>
                <p className="text-stone-500 mb-6" data-id="element-1595">
                  {isReadOnly ? 'Der er ingen kunder i systemet' : 'Tilføj din første kunde for at komme i gang'}
                </p>
                {!isReadOnly && <Button onClick={onOpenCustomerModal} size="sm" data-id="element-1596">
                    Tilføj kunde
                  </Button>}
              </div> : filteredCustomers.length === 0 ? <div className="col-span-full bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1597">
                <SearchIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1598" />
                <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1599">
                  Ingen kunder matcher din søgning
                </h3>
                <p className="text-stone-500" data-id="element-1600">Prøv at justere din søgning</p>
              </div> : filteredCustomers.map((customer, index) => <CustomerCard key={customer.id} customer={customer} index={index} onEdit={onEditCustomer} onDelete={onDeleteCustomer} onViewDetail={onViewCustomerDetail} readOnly={isReadOnly} data-id="element-1601" />)}
          </div>
        </section>
      </div>;
  }
  // Render tasks only view
  return <div data-id="element-1602">
      <header className="mb-8" data-id="element-1603">
        <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1604">Opgaver</h1>
        <p className="text-stone-600" data-id="element-1605">
          Hold styr på alle dine opgaver og deres status
        </p>
      </header>

      <section data-id="element-1606">
        <div className="flex items-center justify-between mb-6" data-id="element-1607">
          <div className="flex items-center gap-3" data-id="element-1608">
            <div className="p-2 bg-amber-100 rounded-lg" data-id="element-1609">
              <ListTodoIcon className="w-5 h-5 text-amber-700" data-id="element-1610" />
            </div>
            <div data-id="element-1611">
              <h2 className="text-2xl font-semibold text-stone-900" data-id="element-1612">Opgaver</h2>
              <p className="text-sm text-stone-500" data-id="element-1613">
                {filteredTasks.length} af {statusCounts.all}
              </p>
            </div>
          </div>
          {!isReadOnly && <Button onClick={onOpenTaskModal} size="sm" className="flex items-center gap-2" disabled={customers.length === 0} data-id="element-1614">
              <PlusIcon className="w-4 h-4" data-id="element-1615" />
              Tilføj opgave
            </Button>}
        </div>

        {/* Category Filter Buttons */}
        <div className="flex gap-2 mb-4" data-id="element-1616">
          <button onClick={() => setTaskFilter('Ladeboks')} className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${taskFilter === 'Ladeboks' ? 'bg-purple-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1617">
            <div className="flex items-center justify-center gap-2" data-id="element-1618">
              <ZapIcon className="w-4 h-4" data-id="element-1619" />
              <span data-id="element-1620">Ladeboks opgaver</span>
            </div>
          </button>
          <button onClick={() => setTaskFilter('Andet')} className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${taskFilter === 'Andet' ? 'bg-teal-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1621">
            <div className="flex items-center justify-center gap-2" data-id="element-1622">
              <PackageIcon className="w-4 h-4" data-id="element-1623" />
              <span data-id="element-1624">Alm. Installation</span>
            </div>
          </button>
        </div>

        {/* Status Filter Buttons */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4" data-id="element-1625">
          <button onClick={() => setStatusFilter('all')} className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${statusFilter === 'all' ? 'bg-stone-900 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1626">
            <div className="flex items-center justify-center gap-2" data-id="element-1627">
              <ListTodoIcon className="w-4 h-4" data-id="element-1628" />
              <span data-id="element-1629">Alle</span>
              <span className="ml-1 text-xs opacity-75" data-id="element-1630">
                ({statusCounts.all})
              </span>
            </div>
          </button>
          <button onClick={() => setStatusFilter('done')} className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${statusFilter === 'done' ? 'bg-emerald-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1631">
            <div className="flex items-center justify-center gap-2" data-id="element-1632">
              <CheckCircleIcon className="w-4 h-4" data-id="element-1633" />
              <span data-id="element-1634">Afsluttet</span>
              <span className="ml-1 text-xs opacity-75" data-id="element-1635">
                ({statusCounts.done})
              </span>
            </div>
          </button>
          <button onClick={() => setStatusFilter('in-progress')} className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${statusFilter === 'in-progress' ? 'bg-amber-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1636">
            <div className="flex items-center justify-center gap-2" data-id="element-1637">
              <PlayCircleIcon className="w-4 h-4" data-id="element-1638" />
              <span data-id="element-1639">I gang</span>
              <span className="ml-1 text-xs opacity-75" data-id="element-1640">
                ({statusCounts['in-progress']})
              </span>
            </div>
          </button>
          <button onClick={() => setStatusFilter('todo')} className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${statusFilter === 'todo' ? 'bg-stone-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1641">
            <div className="flex items-center justify-center gap-2" data-id="element-1642">
              <ClockIcon className="w-4 h-4" data-id="element-1643" />
              <span data-id="element-1644">Afventende</span>
              <span className="ml-1 text-xs opacity-75" data-id="element-1645">
                ({statusCounts.todo})
              </span>
            </div>
          </button>
          <button onClick={() => setStatusFilter('planned')} className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${statusFilter === 'planned' ? 'bg-blue-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1646">
            <div className="flex items-center justify-center gap-2" data-id="element-1647">
              <CalendarIcon className="w-4 h-4" data-id="element-1648" />
              <span data-id="element-1649">Planlagt</span>
              <span className="ml-1 text-xs opacity-75" data-id="element-1650">
                ({statusCounts.planned})
              </span>
            </div>
          </button>
          <button onClick={() => setStatusFilter('pending')} className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${statusFilter === 'pending' ? 'bg-purple-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1651">
            <div className="flex items-center justify-center gap-2" data-id="element-1652">
              <ClockIcon className="w-4 h-4" data-id="element-1653" />
              <span data-id="element-1654">Afventende</span>
              <span className="ml-1 text-xs opacity-75" data-id="element-1655">
                ({statusCounts.pending})
              </span>
            </div>
          </button>
        </div>

        {/* Task Search */}
        {tasks.length > 0 && <div className="mb-4 relative" data-id="element-1656">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-1657" />
            <input type="text" placeholder="Søg efter opgave..." value={taskSearch} onChange={e => setTaskSearch(e.target.value)} className="w-full pl-11 pr-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1658" />
          </div>}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1659">
          {tasks.length === 0 ? <div className="col-span-full bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1660">
              <ListTodoIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1661" />
              <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1662">
                Ingen opgaver endnu
              </h3>
              <p className="text-stone-500 mb-6" data-id="element-1663">
                {isReadOnly ? 'Der er ingen opgaver i systemet' : customers.length === 0 ? 'Tilføj en kunde først, derefter kan du oprette opgaver' : 'Tilføj din første opgave for at komme i gang'}
              </p>
              {!isReadOnly && customers.length > 0 && <Button onClick={onOpenTaskModal} size="sm" data-id="element-1664">
                  Tilføj opgave
                </Button>}
            </div> : filteredTasks.length === 0 ? <div className="col-span-full bg-white rounded-xl p-12 text-center border border-stone-100" data-id="element-1665">
              <SearchIcon className="w-12 h-12 text-stone-300 mx-auto mb-4" data-id="element-1666" />
              <h3 className="text-lg font-medium text-stone-900 mb-2" data-id="element-1667">
                Ingen opgaver matcher dine filtre
              </h3>
              <p className="text-stone-500" data-id="element-1668">
                Prøv at justere kategori, status eller søgning
              </p>
            </div> : filteredTasks.map((task, index) => <TaskCard key={task.id} task={task} index={index} onClick={() => onEditTask(task)} onDelete={onDeleteTask} readOnly={isReadOnly} data-id="element-1669" />)}
        </div>
      </section>
    </div>;
}